import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { useParams, useNavigate, useLocation, useSearchParams, Link } from "react-router-dom"
import { SEO } from "@/components/SEO"
import GithubSlugger from "github-slugger"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import rehypeSlug from "rehype-slug"
import { remarkTableWidths } from "@/lib/remark-table-widths"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { visibleDocs, getDocBySlug, getPrevDoc, getNextDoc } from "@/config/docs"
import { DocsSearch } from "@/components/docs/DocsSearch"
import { ChevronLeft, ChevronRight, ChevronDown, X, Printer, List } from "lucide-react"

export function DocsPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [content, setContent] = useState<string>("")
  const [loading, setLoading] = useState(true)
  const articleRef = useRef<HTMLElement>(null)
  const searchQuery = searchParams.get("q") || ""

  // Extract headings from raw markdown for TOC
  const headings = useMemo(() => {
    if (!content) return []
    const slugger = new GithubSlugger()
    const entries: { level: 2 | 3; text: string; id: string }[] = []
    for (const line of content.split("\n")) {
      const match = line.match(/^(#{2,3})\s+(.+)$/)
      if (match) {
        const level = match[1].length as 2 | 3
        const text = match[2]
          .replace(/\*\*(.+?)\*\*/g, "$1")
          .replace(/\*(.+?)\*/g, "$1")
          .replace(/`(.+?)`/g, "$1")
          .replace(/\[(.+?)\]\(.+?\)/g, "$1")
          .trim()
        entries.push({ level, text, id: slugger.slug(text) })
      }
    }
    return entries
  }, [content])

  // Get current doc from URL slug or default to first doc
  const currentDoc = slug ? getDocBySlug(slug) : visibleDocs[0]
  const actualDoc = currentDoc || visibleDocs[0]

  // Get previous and next docs for navigation
  const prevDoc = getPrevDoc(actualDoc)
  const nextDoc = getNextDoc(actualDoc)

  useEffect(() => {
    const fetchMarkdown = async () => {
      setLoading(true)
      try {
        const response = await fetch(actualDoc.path)
        if (!response.ok) {
          throw new Error(`Failed to fetch ${actualDoc.path}`)
        }
        const text = await response.text()
        setContent(text)
      } catch (error) {
        console.error("Error loading markdown:", error)
        setContent("# Error\n\nFailed to load document.")
      } finally {
        setLoading(false)
      }
    }

    fetchMarkdown()
  }, [actualDoc.path])

  // Redirect to first doc if slug not found
  useEffect(() => {
    if (slug && !currentDoc) {
      navigate("/docs/overview", { replace: true })
    }
  }, [slug, currentDoc, navigate])

  // Scroll to hash anchor after content loads
  useEffect(() => {
    if (!loading && location.hash) {
      const id = location.hash.slice(1)
      // Small delay to ensure markdown is fully rendered
      setTimeout(() => {
        const element = document.getElementById(id)
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" })
        }
      }, 100)
    }
  }, [loading, location.hash])

  // Clear search highlights
  const clearHighlights = useCallback(() => {
    // Navigate to clean URL without query params
    navigate(`/docs/${actualDoc.slug}${location.hash}`, { replace: true })
  }, [navigate, actualDoc.slug, location.hash])

  // Highlight search terms after content loads
  useEffect(() => {
    if (loading || !searchQuery || !articleRef.current) return

    // Small delay to ensure markdown is fully rendered
    const timer = setTimeout(() => {
      if (!articleRef.current) return

      // Split query into words for individual highlighting
      const terms = searchQuery.toLowerCase().split(/\s+/).filter(t => t.length > 1)
      if (terms.length === 0) return

      // Walk text nodes and wrap matches with <mark>
      const walker = document.createTreeWalker(
        articleRef.current,
        NodeFilter.SHOW_TEXT,
        null
      )

      const nodesToProcess: { node: Text; matches: { start: number; end: number }[] }[] = []

      let textNode: Text | null
      while ((textNode = walker.nextNode() as Text | null)) {
        const text = textNode.textContent || ""
        const lowerText = text.toLowerCase()
        const matches: { start: number; end: number }[] = []

        for (const term of terms) {
          let pos = 0
          while ((pos = lowerText.indexOf(term, pos)) !== -1) {
            matches.push({ start: pos, end: pos + term.length })
            pos += term.length
          }
        }

        if (matches.length > 0) {
          // Sort by position and merge overlapping
          matches.sort((a, b) => a.start - b.start)
          const merged: { start: number; end: number }[] = []
          for (const m of matches) {
            const last = merged[merged.length - 1]
            if (last && m.start <= last.end) {
              last.end = Math.max(last.end, m.end)
            } else {
              merged.push({ ...m })
            }
          }
          nodesToProcess.push({ node: textNode, matches: merged })
        }
      }

      // Apply highlights (in reverse to preserve positions)
      for (const { node, matches } of nodesToProcess.reverse()) {
        const text = node.textContent || ""
        const fragment = document.createDocumentFragment()
        let lastEnd = 0

        for (const { start, end } of matches) {
          if (start > lastEnd) {
            fragment.appendChild(document.createTextNode(text.slice(lastEnd, start)))
          }
          const mark = document.createElement("mark")
          mark.className = "bg-yellow-300 dark:bg-yellow-600 text-foreground rounded px-0.5"
          mark.textContent = text.slice(start, end)
          fragment.appendChild(mark)
          lastEnd = end
        }

        if (lastEnd < text.length) {
          fragment.appendChild(document.createTextNode(text.slice(lastEnd)))
        }

        node.parentNode?.replaceChild(fragment, node)
      }

      // Scroll first highlight into view
      const firstMark = articleRef.current.querySelector("mark")
      if (firstMark) {
        setTimeout(() => {
          firstMark.scrollIntoView({ behavior: "smooth", block: "center" })
        }, 50)
      }
    }, 150)

    return () => clearTimeout(timer)
  }, [loading, searchQuery, content])

  return (
    <>
      <SEO
        title={actualDoc.label}
        url={`/docs/${actualDoc.slug}`}
        description={actualDoc.description}
        keywords={`${actualDoc.label.toLowerCase()}, WELEPHANT, prediction mining docs, blockchain gaming`}
      />
      <div className="container py-2">
        <div className="max-w-4xl mx-auto">
          {/* Navigation dropdown, TOC, and search - sticky below header */}
          <div className="sticky top-16 z-40 bg-background py-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      {actualDoc.label}
                      <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {visibleDocs.map((doc) => (
                      <DropdownMenuItem key={doc.slug} asChild>
                        <Link
                          to={`/docs/${doc.slug}`}
                          className={actualDoc.slug === doc.slug ? "font-medium" : ""}
                        >
                          {doc.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {headings.length > 0 && (
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <List className="h-4 w-4" />
                        <span className="hidden sm:inline ml-1">Contents</span>
                        <ChevronDown className="h-3 w-3 ml-1 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto w-72">
                      {headings.map((heading) => (
                        <DropdownMenuItem
                          key={heading.id}
                          onClick={() => {
                            const el = document.getElementById(heading.id)
                            if (el) {
                              el.scrollIntoView({ behavior: "smooth", block: "start" })
                              window.history.replaceState(null, "", `#${heading.id}`)
                            }
                            // Remove focus highlight from the trigger button after selection
                            setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 0)
                          }}
                          className={heading.level === 3 ? "pl-6 text-muted-foreground" : "font-medium"}
                        >
                          <span className="truncate">{heading.text}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              <div className="flex items-center gap-2">
                {searchQuery && (
                  <button
                    onClick={clearHighlights}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground bg-muted px-2 py-1 rounded"
                  >
                    <span>Clear "{searchQuery}"</span>
                    <X className="h-3 w-3" />
                  </button>
                )}
                <DocsSearch />
              </div>
            </div>
          </div>

          {/* Content */}
          <Card className="p-6 md:p-8">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="text-muted-foreground">Loading...</div>
              </div>
            ) : (
              <article ref={articleRef} className="print-content prose prose-neutral dark:prose-invert max-w-full [overflow-wrap:anywhere]">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkTableWidths]}
                  rehypePlugins={[rehypeSlug]}
                  components={{
                    h1: ({ children, id }) => (
                      <div className="flex items-start justify-between gap-2 mb-4 scroll-mt-32" id={id}>
                        <h1 className="text-3xl font-bold tracking-tight">
                          {children}
                        </h1>
                        <Button variant="ghost" size="icon" className="print:hidden shrink-0 mt-1" onClick={() => window.print()}>
                          <Printer className="h-4 w-4" />
                        </Button>
                      </div>
                    ),
                    h2: ({ children, id }) => (
                      <h2 id={id} className="text-2xl font-semibold mt-8 mb-4 border-b pb-2 scroll-mt-32">
                        {children}
                      </h2>
                    ),
                    h3: ({ children, id }) => (
                      <h3 id={id} className="text-xl font-semibold mt-6 mb-3 scroll-mt-32">
                        {children}
                      </h3>
                    ),
                    p: ({ children }) => (
                      <p className="text-muted-foreground leading-7 mb-4">
                        {children}
                      </p>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4">
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal list-inside space-y-2 text-muted-foreground mb-4">
                        {children}
                      </ol>
                    ),
                    li: ({ children }) => (
                      <li className="text-muted-foreground">{children}</li>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground my-4">
                        {children}
                      </blockquote>
                    ),
                    code: ({ className, children }) => {
                      const isInline = !className
                      return isInline ? (
                        <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">
                          {children}
                        </code>
                      ) : (
                        <code className="block bg-muted p-4 rounded-lg text-sm font-mono overflow-x-auto my-4">
                          {children}
                        </code>
                      )
                    },
                    pre: ({ children }) => (
                      <pre className="bg-muted p-4 rounded-lg overflow-x-auto my-4">
                        {children}
                      </pre>
                    ),
                    table: ({ children, node }) => {
                      // Check for column widths from remark-table-widths plugin
                      const widthsAttr = node?.properties?.["data-col-widths"] as string | undefined
                      const widths = widthsAttr?.split(",").map(w => w ? parseInt(w, 10) : null)

                      return (
                        <div className="overflow-x-auto my-4 max-w-full">
                          <table className="min-w-full border-collapse table-fixed">
                            {widths && widths.some(w => w !== null) && (
                              <colgroup>
                                {widths.map((w, i) => (
                                  <col key={i} style={w ? { width: `${w}%` } : undefined} />
                                ))}
                              </colgroup>
                            )}
                            {children}
                          </table>
                        </div>
                      )
                    },
                    thead: ({ children }) => (
                      <thead className="bg-muted">{children}</thead>
                    ),
                    th: ({ children }) => (
                      <th className="border px-4 py-2 text-left font-semibold">
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td className="border px-4 py-2 text-muted-foreground">
                        {children}
                      </td>
                    ),
                    hr: () => <hr className="my-8 border-border" />,
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        className="text-primary hover:underline"
                        target={href?.startsWith("http") ? "_blank" : undefined}
                        rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
                      >
                        {children}
                      </a>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold text-foreground">
                        {children}
                      </strong>
                    ),
                    img: ({ src, alt }) => (
                      <img
                        src={src}
                        alt={alt || ""}
                        className="max-w-full h-auto rounded-lg my-4"
                      />
                    ),
                  }}
                >
                  {content}
                </ReactMarkdown>
              </article>
            )}

            {/* Prev / Next navigation */}
            {!loading && (
              <nav className="flex justify-between mt-8 pt-6 border-t">
                <div>
                  {prevDoc && (
                    <Button variant="ghost" asChild>
                      <Link to={`/docs/${prevDoc.slug}`}>
                        <ChevronLeft />
                        {prevDoc.label}
                      </Link>
                    </Button>
                  )}
                </div>
                <div>
                  {nextDoc && (
                    <Button variant="ghost" asChild>
                      <Link to={`/docs/${nextDoc.slug}`}>
                        {nextDoc.label}
                        <ChevronRight />
                      </Link>
                    </Button>
                  )}
                </div>
              </nav>
            )}
          </Card>
        </div>
      </div>
    </>
  )
}
